import React, { useEffect, useState, useContext } from "react";
import { AiFillCloseCircle } from "react-icons/ai";
import { userUpdate } from "../actions/userUpdate";
import { UserContext } from "../context/MainContext";
import InputField, { TextareaField } from "./InputField";

const AdminUserEditModal = ({ modal, setModal, selectedUser, onUpdate }) => {
  const [form, setForm] = useState({});
  const { user } = useContext(UserContext);

  const normalizeHandle = (val) => {
    if (!val) return val;
    return val.trim().replace(/^@+/, "");
  };

  const ensureScheme = (url) => {
    if (!url) return url;
    const v = url.trim();
    if (/^https?:\/\//i.test(v)) return v.replace(/^http:\/\//i, "https://");
    return `https://${v}`;
  };

  const normalizeSocial = (value, platform) => {
    if (!value) return value;
    const v = value.trim();
    if (/^https?:\/\//i.test(v) || /^www\./i.test(v)) {
      return ensureScheme(v);
    }
    if (
      v.match(/(instagram\.com|facebook\.com|twitter\.com|x\.com|tiktok\.com)/i)
    ) {
      return ensureScheme(v);
    }
    const handle = normalizeHandle(v);
    switch (platform) {
      case "instagram":
        return `https://www.instagram.com/${handle}`;
      case "facebook":
        return `https://www.facebook.com/${handle}`;
      case "twitter":
      case "x":
        return `https://x.com/${handle}`;
      case "tiktok":
        return `https://www.tiktok.com/@${handle}`;
      default:
        return value;
    }
  };

  useEffect(() => {
    setForm(selectedUser ? { ...selectedUser } : {});
  }, [modal, selectedUser]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleUpdate = async () => {
    const normalized = { ...form };
    normalized.facebook = normalizeSocial(form?.facebook, "facebook");
    normalized.instagram = normalizeSocial(form?.instagram, "instagram");
    normalized.twitter = normalizeSocial(form?.twitter, "x");
    normalized.tiktok = normalizeSocial(form?.tiktok, "tiktok");
    if (form?.video && !/^https?:\/\//i.test(form.video)) {
      normalized.video = ensureScheme(form.video);
    }

    const changes = {};
    Object.keys(normalized).forEach((k) => {
      const newVal = normalized[k];
      const oldVal = selectedUser?.[k];
      const changed = String(newVal || "") !== String(oldVal || "");
      if (changed) changes[k] = newVal;
    });

    if (Object.keys(changes).length > 0) {
      changes.user_id = selectedUser.user_id;
      // If current user is an admin, include admin identifiers for auditing/server-side checks
      if (user && user.user_id) {
        changes.admin_user_id = user.user_id;
        changes.admin_email = user.email_address || user.email;
      }
      await userUpdate(changes, () => {});
      if (onUpdate) onUpdate();
    }
    setModal(false);
  };

  if (!modal) return null;

  return (
    <>
      <div className="flex bg-black/40 w-[100%] top-0 left-0 h-screen fixed z-50"></div>
      <div className="flex justify-center max-w-[1950px] top-32 absolute z-50 tablet:px-16 homebreak:px-32 w-full">
        <div className="bg-white flex flex-col p-4 pt-6 px-14 text-center relative w-[800px] max-h-[80vh] overflow-y-auto">
          <div
            className="absolute right-4 top-4 hover:scale-105 hover:cursor-pointer transition-all"
            onClick={() => setModal(false)}
          >
            <AiFillCloseCircle size={30} />
          </div>
          <h1 className="mb-2 text-30">Edit User Profile (Admin)</h1>
          <hr />
          <p className="my-2 text-xs">
            Editing user: {selectedUser?.email_address}
          </p>
          <div className="flex flex-col gap-4 mt-4">
            <div className="flex gap-4 flex-wrap">
              <div className="flex flex-col flex-1 min-w-[200px]">
                <label className="text-left text-black font-medium">
                  Legal First Name
                </label>
                <input
                  className="bg-slate-300 py-[10px] px-2 rounded"
                  name="legal_first_name"
                  value={form?.legal_first_name || ""}
                  onChange={handleChange}
                />
              </div>
              <div className="flex flex-col flex-1 min-w-[200px]">
                <label className="text-left text-black font-medium">
                  Legal Last Name
                </label>
                <input
                  className="bg-slate-300 py-[10px] px-2 rounded"
                  name="legal_last_name"
                  value={form?.legal_last_name || ""}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="flex gap-4 flex-wrap">
              <div className="flex flex-col flex-1 min-w-[200px]">
                <label className="text-left text-black font-medium">
                  Email Address
                </label>
                <InputField
                  className="bg-slate-300 py-[10px] px-2 rounded"
                  name="email_address"
                  value={form?.email_address || ""}
                  onChange={handleChange}
                  label="Email Address"
                />
              </div>
              <div className="flex flex-col flex-1 min-w-[200px]">
                <label className="text-left text-black font-medium">
                  Phone Number
                </label>
                <InputField
                  className="bg-slate-300 py-[10px] px-2 rounded"
                  name="phone_number"
                  value={form?.phone_number || ""}
                  onChange={handleChange}
                  label="Phone Number"
                />
              </div>
            </div>

            <div className="flex gap-4 flex-wrap">
              <div className="flex flex-col flex-1 min-w-[200px]">
                <label className="text-left text-black font-medium">
                  Profession
                </label>
                <InputField
                  className="bg-slate-300 py-[10px] px-2 rounded"
                  name="class_type"
                  value={form?.class_type || ""}
                  onChange={handleChange}
                  label="Profession"
                />
              </div>
              <div className="flex flex-col flex-1 min-w-[200px]">
                <label className="text-left text-black font-medium">
                  Keys Balance
                </label>
                <InputField
                  className="bg-slate-300 py-[10px] px-2 rounded"
                  name="keys"
                  type="number"
                  value={form?.keys || 0}
                  onChange={handleChange}
                  label="Keys Balance"
                />
              </div>
            </div>

            <div className="flex flex-col">
              <label className="text-left text-black font-medium">
                About Description
              </label>
              <TextareaField
                className="bg-slate-300 px-4 py-2 rounded min-h-[80px]"
                name="about"
                value={form?.about || ""}
                onChange={handleChange}
                label="About Description"
              />
            </div>

            {(form?.class_type === "Realtor" ||
              form?.class_type === "Investor" ||
              form?.class_type === "Lender") && (
              <>
                <div className="flex flex-col">
                  <label className="text-left text-black font-medium">
                    Brokerage Name
                  </label>
                  <InputField
                    className="bg-slate-300 px-4 py-2 rounded"
                    name="brokerage_name"
                    value={form?.brokerage_name || ""}
                    onChange={handleChange}
                    label="Brokerage Name"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="text-left text-black font-medium">
                    Brokerage Number
                  </label>
                  <InputField
                    className="bg-slate-300 px-4 py-2 rounded"
                    name="brokerage_number"
                    value={form?.brokerage_number || ""}
                    onChange={handleChange}
                    label="Brokerage Number"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="text-left text-black font-medium">
                    Broker Name
                  </label>
                  <InputField
                    className="bg-slate-300 px-4 py-2 rounded"
                    name="broker_name"
                    value={form?.broker_name || ""}
                    onChange={handleChange}
                    label="Broker Name"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="text-left text-black font-medium">
                    Broker Number
                  </label>
                  <InputField
                    className="bg-slate-300 px-4 py-2 rounded"
                    name="broker_number"
                    value={form?.broker_number || ""}
                    onChange={handleChange}
                    label="Broker Number"
                  />
                </div>
              </>
            )}

            {form?.class_type === "Builder" && (
              <>
                <div className="flex flex-col">
                  <label className="text-left text-black font-medium">
                    Builder Company Name
                  </label>
                  <InputField
                    className="bg-slate-300 px-4 py-2 rounded"
                    name="builder_company_name"
                    value={form?.builder_company_name || ""}
                    onChange={handleChange}
                    label="Builder Company Name"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="text-left text-black font-medium">
                    Builder Company Supervisor
                  </label>
                  <InputField
                    className="bg-slate-300 px-4 py-2 rounded"
                    name="builder_company_supervisor"
                    value={form?.builder_company_supervisor || ""}
                    onChange={handleChange}
                    label="Builder Company Supervisor"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="text-left text-black font-medium">
                    Direct Office Contact #
                  </label>
                  <InputField
                    className="bg-slate-300 px-4 py-2 rounded"
                    name="direct_office"
                    value={form?.direct_office || ""}
                    onChange={handleChange}
                    label="Direct Office Contact"
                  />
                </div>
              </>
            )}

            <div className="flex flex-col">
              <label className="text-left text-black font-medium">
                Facebook URL
              </label>
              <InputField
                className="bg-slate-300 px-4 py-2 rounded"
                name="facebook"
                value={form?.facebook || ""}
                onChange={handleChange}
                label="Facebook URL"
              />
            </div>
            <div className="flex flex-col">
              <label className="text-left text-black font-medium">
                Instagram URL
              </label>
              <InputField
                className="bg-slate-300 px-4 py-2 rounded"
                name="instagram"
                value={form?.instagram || ""}
                onChange={handleChange}
                label="Instagram URL"
              />
            </div>
            <div className="flex flex-col">
              <label className="text-left text-black font-medium">
                X (Twitter) URL
              </label>
              <InputField
                className="bg-slate-300 px-4 py-2 rounded"
                name="twitter"
                value={form?.twitter || ""}
                onChange={handleChange}
                label="X (Twitter) URL"
              />
            </div>
            <div className="flex flex-col">
              <label className="text-left text-black font-medium">
                Tiktok URL
              </label>
              <InputField
                className="bg-slate-300 px-4 py-2 rounded"
                name="tiktok"
                value={form?.tiktok || ""}
                onChange={handleChange}
                label="TikTok URL"
              />
            </div>
            <div className="flex flex-col">
              <label className="text-left text-black font-medium">
                Embed Video On Profile
              </label>
              <InputField
                className="bg-slate-300 px-4 py-2 rounded"
                name="video"
                value={form?.video || ""}
                onChange={handleChange}
                label="Embed Video On Profile"
              />
            </div>

            <button
              className="bg-blue-500 text-white rounded-10 py-2 mt-4 mb-4 hover:scale-105 hover:cursor-pointer transition-all font-semibold"
              onClick={handleUpdate}
            >
              Update User
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminUserEditModal;
